import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Scroll, Heart, Shield, Gift } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import CategoryContactForm from "../components/CategoryContactForm";
import { useLanguage } from "../contexts/LanguageContext";

const LegacyPage = () => {
  const { t, lang } = useLanguage();

  useEffect(() => {
    document.title = lang === "en" ? "Leave a legacy – Fondation Cœur Solidaire" : "Faire un legs – Fondation Cœur Solidaire";
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="legacy-page">
      <Seo
        title={t.seo.legacy.title}
        description={t.seo.legacy.description}
        path={lang === "en" ? "/legacy" : "/legs"}
        lang={lang}
      />
      <Navbar />

      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-[#002B49] to-[#001F35] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #C5A059 0%, transparent 50%)" }} />
        <div className="container-narrow relative">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-[#E8C77E] mb-6 text-sm transition-colors">
            <ArrowLeft size={14} /> {t.legacyPage.back}
          </Link>
          <span className="eyebrow text-[#E8C77E]">{t.legacyPage.eyebrow}</span>
          <span className="block w-16 h-px bg-[#C5A059] mt-4 mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1]" data-testid="legacy-title">
            {t.legacyPage.title}
          </h1>
          <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">
            {t.legacyPage.intro}
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="space-y-8">
              {t.legacyPage.options.map((opt, i) => {
                const icons = [Scroll, Gift, Heart];
                const Icon = icons[i] || Heart;
                return (
                  <motion.div
                    key={opt.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="flex gap-4"
                    data-testid={`legacy-option-${i}`}
                  >
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-[#FDF8EE] flex items-center justify-center">
                      <Icon size={20} className="text-[#C5A059]" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-[#002B49] font-semibold">{opt.title}</h3>
                      <p className="text-[#64748B] text-sm leading-relaxed mt-1.5">{opt.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-10 p-6 bg-[#FDF8EE] border border-[#C5A059]/30 rounded-xl">
              <Shield size={18} className="text-[#C5A059] mb-2" />
              <div className="font-serif text-lg text-[#002B49] font-semibold">{t.legacyPage.trustTitle}</div>
              <p className="text-sm text-[#334155] mt-1.5 leading-relaxed">{t.legacyPage.trustText}</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <h2 className="font-serif text-3xl text-[#002B49] mb-2 font-semibold">{t.legacyPage.formTitle}</h2>
            <p className="text-[#64748B] mb-6">{t.legacyPage.formSubtitle}</p>
            <CategoryContactForm
              category="legacy"
              t={t.legacyPage.form}
              defaultSubject={t.legacyPage.defaultSubject}
              testid="legacy-form"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LegacyPage;
